<!-- =========================================================================================
  File Name: App.vue
  Description: Main vue file - APP
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->


<template>
  <div id="app">
    <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet">
    <!--<vs-alert v-if="!HasExtension" color="primary" icon="warning" :active.sync="HasExtension" closable style="height:auto !important;" active="true" class="global-info text-white pb-5">
      We highly recommend to download the Chrome / Edge extension for best experience at <a class="text-white" href="https://chromewebstore.google.com/detail/tcvelik-assistant/cfjhapiajpbhemmjjjhjppknflfbhmcj?authuser=1&hl=en-GB">Chrome Store</a>
    </vs-alert>-->
    <vs-alert v-if="validatedUser" ref="globalAlert" class="global-component mb-5 text-white" :active.sync="validatedUser" closable>
      {{$t('NoPasswordWarning')}} <strong><a class="cursor-pointer text-white underline" @click.stop="resetPassword()">{{$t('CreatePasswordCTA')}}</a></strong>
    </vs-alert>

    <vs-alert v-if="licenseWarning" ref="licenseAlert" class="global-component mb-5 text-white" :active.sync="licenseWarning" closable>
      <strong>{{$t('LicenseWarningTitle')}}</strong> {{ licenseWarningMessage }}
      <strong><a class="cursor-pointer text-white underline" @click.stop="goToLicensing()">{{$t('ManageLicenseCTA')}}</a></strong>
    </vs-alert>

    <!-- License Conflict Modal -->
    <LicenseConflictModal :visible.sync="showLicenseConflictModal"
                          :error-message="licenseConflictMessage"
                          :alternative-licenses="alternativeLicenses"
                          @close="closeLicenseConflictModal"
                          @license-switched="onLicenseSwitched" />
    <router-view></router-view>
  </div>
</template>

<script>
  var instance = this;

  // import themeConfig from "@/../themeConfig.js";
  // import Vue from "vue";
  import ClientInteropabilityService from "@/interopability/clientInteropabilityService.js";
  import accountsService from "./accounts/accountsService";
  import TCVelikClient from '@/TCVelikClient/TCVelikClient.js';

  export default {
    data() {
      return {
        inExtension: false,
        validatedUser: false,
        HasExtension: true,
        licenseWarning: false,
        licenseWarningMessage: '',
      }
    },
    watch: {
      "$store.state.theme"(val) {
        this.toggleClassInBody(val);
      },
      validatedUser(newValue, oldValue) {
        if (newValue == false) {
          document.body.classList.remove("has-alert");
        }
      },
      "$route"(to, from) {
        // Hide license warning on specific pages
        if (to && this.shouldHideLicenseWarning(to.path)) {
          this.licenseWarning = false;
        } else {
          // Re-check license status when navigating to pages that should show warnings
          this.checkLicenseStatus();
        }
      }
    },
    async created() {
      // Check if we should hide license warning on current route
      if (this.shouldHideLicenseWarning(this.$route.path)) {
        this.licenseWarning = false;
      } else {
        // Check license status on app startup
        this.checkLicenseStatus();
      }
    },
    methods: {
      shouldHideLicenseWarning(path) {
        // Hide license warning on these specific pages
        const hideOnPaths = [
          '/pages/login',
          '/pages/register',
          '/pages/forgot-password',
          '/pages/reset-password',
          '/licensing',
          '/licensing/catalog',
          '/onboarding-pdf'
        ];
        
        return hideOnPaths.some(hidePath => path.includes(hidePath));
      },
      toggleClassInBody(className) {
        if (className == "dark") {
          if (document.body.className.match("theme-semi-dark"))
            document.body.classList.remove("theme-semi-dark");
          document.body.classList.add("theme-dark");
        } else if (className == "semi-dark") {
          if (document.body.className.match("theme-dark"))
            document.body.classList.remove("theme-dark");
          document.body.classList.add("theme-semi-dark");
        } else {
          if (document.body.className.match("theme-dark"))
            document.body.classList.remove("theme-dark");
          if (document.body.className.match("theme-semi-dark"))
            document.body.classList.remove("theme-semi-dark");
        }
      },
      handleWindowResize(event) {
        this.$store.dispatch("updateWindowWidth", event.currentTarget.innerWidth);
      },
      handlePostMessage(event) {
        if (event.data.type !== 'webpackOk') {
          if (event.data.type == 'extensionMode') {
            this.inExtension = true;
            document.body.setAttribute("notcv", true);
          }
        }
      },

      handleMessageFromParent(event) {
        // Validate the origin if necessary
        const message = event.data;
        if (message.type === 'rpc' && this.rpcMethods[message.methodName]) {
          const result = this.rpcMethods[message.methodName](message.args);
          // Send response back to the parent window
          window.parent.postMessage({
            type: 'rpcResponse',
            methodName: message.methodName,
            result
          }, '*');
        }
      },
      checkForTcvelikExtension(timeout = 1000) {
        var instance = this;
        return new Promise((resolve) => {
          const listener = (event) => {
            if (event.source !== window) return;
            if (event.data && event.data.type === 'TCVELIK_PONG') {
              window.removeEventListener('message', listener);
              resolve(true);
            }
          };

          window.addEventListener('message', listener);

          // Send the ping
          window.postMessage({ type: 'TCVELIK_PING' }, '*');

          // Set timeout for fallback
          setTimeout(() => {
            window.removeEventListener('message', listener);
            resolve(false);
          }, timeout);
        });
      },

      async initTcvelikCheck() {

        const lastPromptStr = localStorage.getItem("TCV_LastExtCheck");
        const now = new Date();
        let shouldCheck = true;

        if (lastPromptStr) {
          const lastPrompt = new Date(lastPromptStr);

          // Compare weeks (same calendar week = skip)
          const nowWeek = this.getYearWeek(now);
          const lastWeek = this.getYearWeek(lastPrompt);

          if (nowWeek.year === lastWeek.year && nowWeek.week === lastWeek.week) {
            shouldCheck = false;
          }
        }

        // Always try auto-login regardless of weekly check
        await this.tryExtensionAutoLogin();

        if (!shouldCheck) {
          console.log("🕐 Extension check skipped (already prompted this week)");
          return;
        }

        localStorage.setItem("TCV_LastExtCheck", now.toISOString());
        const extensionDetected = await this.checkForTcvelikExtension();
        this.$isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (extensionDetected) {
          this.tryExtensionAutoLogin();
          console.log('✅ TCVelik extension is installed');
        } else if (!extensionDetected && !this.$isMobile && !this.$isAnonymous) {
          this.HasExtension = false;

            this.$vs.notify({
              title: this.$t('TCVelikTip'),
            time: 20000,
            color: "primary",
              text: this.$t('RecommendExtensionText'),
            click: () => {
              var a = document.createElement("a");
              a.href = "https://chromewebstore.google.com/detail/tcvelik-assistant/cfjhapiajpbhemmjjjhjppknflfbhmcj?authuser=1&hl=en-GB";
              a.target = "_blank";
              a.click();
              //window.location.href = ;
            },
            iconPack: "feather",
            position: "top-center",
            icon: "icon-check-circle",
          });
        }
      },

      async tryExtensionAutoLogin() {
        // Check if user is not logged in
        const userInfo = localStorage.getItem(location.hostname + '-fullUserInfo');
        if (userInfo) {
          console.log('ℹ️ User already logged in, skipping auto-login');
          return;
        }

        // Try to detect extension and auto-login
        const extensionDetected = await this.checkForTcvelikExtension();
        if (extensionDetected) {
          console.log('✅ TCVelik extension detected, attempting auto-login...');
          const autoLoginSuccess = await this.attemptExtensionAutoLogin();
          if (!autoLoginSuccess) {
            console.log('ℹ️ Auto-login not available or failed');
          }
        } else {
          console.log('ℹ️ TCVelik extension not detected, skipping auto-login');
        }
      },
      async attemptExtensionAutoLogin() {
        try {
          console.log('🔍 Attempting auto-login via TCVelik extension...');

          // Request auth data from extension
          const userInfo = await this.getExtensionAuthData();

          if (userInfo && userInfo.token) {
            console.log('✅ Auto-login successful via extension');

            // Store the auth data in the web app's localStorage
            localStorage.setItem(location.hostname + "-token", userInfo.token);
            localStorage.setItem(location.hostname + '-fullUserInfo' , JSON.stringify(userInfo));

            // Update Vuex store
            this.$store.commit("IS_GUEST_USER", userInfo.User?.isGuestUser == true);
            this.$store.commit("CURRENT_USER", userInfo.User);

            // Show success notification
            this.$vs.notify({
              title: this.$t('AutoLoginSuccessful'),
              text: this.$t('AutoLoginSuccessfulText'),
              time: 4000,
              color: "success",
              iconPack: "feather",
              position: "top-center",
              icon: "icon-check-circle",
            });

            // Handle verified user alert if needed
            if (userInfo.VerifiedUser != null && userInfo.VerifiedUser == false && window.location.pathname != "/pages/forgot-password") {
              this.validatedUser = true;
              this.$nextTick(() => {
                const globalAlert = this.$refs.globalAlert?.$el;
                if (globalAlert) {
                  document.body.classList.add("has-alert");
                  document.body.insertBefore(globalAlert, document.body.firstChild);
                }
              });
            }

            return true;
          } else {
            console.log('ℹ️ No valid auth data found in extension');
            return false;
          }
        } catch (error) {
          console.error('❌ Error during auto-login attempt:', error);
          return false;
        }
      },

      getExtensionAuthData() {
        return new Promise((resolve) => {
          const timeout = setTimeout(() => {
            window.removeEventListener('message', listener);
            resolve(null);
          }, 2000); // 2 second timeout

          const listener = (event) => {
            if (event.source !== window) return;
            if (event.data && event.data.type === 'TCVELIK_AUTH_RESPONSE') {
              clearTimeout(timeout);
              window.removeEventListener('message', listener);
              resolve(event.data.userInfo);
            }
          };

          window.addEventListener('message', listener);

          // Request auth data from extension
          window.postMessage({
            type: 'TCVELIK_GET_AUTH_DATA',
            requestId: Date.now()
          }, '*');
        });
      },
      getYearWeek(date) {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
        return { year: d.getUTCFullYear(), week: weekNo };
      },
      resetPassword() {
        var userInfo = localStorage.getItem(location.hostname + "-fullUserInfo");
        var userObject = JSON.parse(userInfo);
        var instance = this;
        accountsService.resetPassword(userObject.User.Email).then(function (response) {
          if (response) {
            instance.validatedUser = false
            instance.$vs.notify({
              title: "An email has been sent with a link to set a new password.",
              text: "Please check your inbox.",
              time: 6000,
              color: "success",
              iconPack: "feather",
              position: "top-center",
              icon: "icon-check-circle",
            });
          }
        })
      },
      async checkLicenseStatus() {
        try {
          // Don't show license warning on licensing pages
          if (this.$route && (this.$route.path.includes('/licensing') || this.$route.path.includes('/pages/licensing'))) {
            this.licenseWarning = false;
            return;
          }

          const response = await TCVelikClient.GetRelative('/api/licensing/status');
          const status = response.data;
          if (!status.IsValid || (status.Edition && status.Edition.toLowerCase() === 'disabled')) {
            // Check if there are alternative licenses available
            if (status.HasAlternativeLicenses && status.AlternativeLicenses && status.AlternativeLicenses.length > 0) {
              // Show license conflict modal instead of warning banner
              this.licenseConflictMessage = status.ErrorMessage || 'License domain conflict detected.';
              this.alternativeLicenses = status.AlternativeLicenses;
              this.showLicenseConflictModal = true;
              this.licenseWarning = false; // Hide the warning banner
            } else {
              // Show regular license warning
              this.licenseWarning = true;
              this.licenseWarningMessage = status.ErrorMessage || 'License required to continue using TCVelik.';
              this.showLicenseConflictModal = false; // Hide modal if it was showing
            }
          } else if (status.DaysUntilExpiry !== undefined && status.DaysUntilExpiry !== null) {
            // Show warning 7 days AFTER expiration (when DaysUntilExpiry is between -14 and -8)
            // DaysUntilExpiry will be negative after expiration, but license is still valid within 14-day grace period
            // Day 1-7: DaysUntilExpiry = -1 to -7 (no warning)
            // Day 8-14: DaysUntilExpiry = -8 to -14 (show warning, 7+ days after expiration)
            if (status.DaysUntilExpiry <= -14) {
              const daysAfterExpiry = Math.abs(status.DaysUntilExpiry);
              this.licenseWarning = true;
              this.licenseWarningMessage = `Your license expired ${daysAfterExpiry} days ago. Please renew to avoid service interruption.`;
              this.showLicenseConflictModal = false;
            } else {
              this.licenseWarning = false;
              this.showLicenseConflictModal = false;
            }
          } else {
            this.licenseWarning = false;
          }
        } catch (error) {
          console.error('Error checking license status:', error);
          // Don't show warning on error to avoid false alarms
        }
      },
      goToLicensing() {
        this.$router.push('/licensing/catalog');
      },

      closeLicenseConflictModal() {
        this.showLicenseConflictModal = false;
        this.alternativeLicenses = [];
        this.licenseConflictMessage = '';
      },

      onLicenseSwitched(licenseData) {
        console.log('License switched successfully:', licenseData);
        // The modal will handle the page refresh
      }
    },
    mounted() {
      ClientInteropabilityService.loadFormFactor(this.$store);
      this.$nextTick(() => {
        window.addEventListener("resize", this.handleWindowResize);
      });


      if (this.$systemSettings && this.$systemSettings.DefaultSystemLanguage && !localStorage.getItem(location.hostname + "-lang-changed")) {
          this.$i18n.locale = this.$systemSettings.DefaultSystemLanguage;
      }

      this.$isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      this.$store.dispatch("updateWindowWidth", window.innerWidth);


      window.addEventListener("message", this.handlePostMessage);

      var userInfo = localStorage.getItem(location.hostname + "-fullUserInfo");

      if (userInfo) {
        try {
          var instance = this;
          var userObject = JSON.parse(userInfo);
          this.$store.commit("IS_GUEST_USER", userObject?.User?.isGuestUser == true);
          this.$store.commit("CURRENT_USER", userObject.User);

          if (userObject.VerifiedUser != null && userObject.VerifiedUser == false && window.location.pathname != "/pages/forgot-password") {
            instance.validatedUser = true
            const globalAlert = this.$refs.globalAlert.$el;
            if (globalAlert) {
              document.body.classList.add("has-alert");
              document.body.insertBefore(globalAlert, document.body.firstChild);
            }
          }
        }
        catch (err) {
          accountsService.cleanUserLocalStorageItems();
          alert('There was an error with your user.\nPlease login again.');
          window.location.href = '/pages/login';
        }
      }


      // Check license status on app load
      this.checkLicenseStatus();

      var instance = this;
      window.setTimeout(function () {
        instance.initTcvelikCheck();
      }, 1000);
    },
    beforeDestroy() {
      window.removeEventListener("resize", this.handleWindowResize);
    }
  };
</script>

<style>
  .global-component {
    position: fixed !important;
    top: 0;
    left: 0;
    z-index: 999999999;
    background: rgba(var(--vs-danger),1) !important;
  }

  .global-info {
    position: fixed !important;
    top: 0;
    left: 0;
    z-index: 999999999;
    background: rgba(var(--vs-primary),1) !important;
    vertical-align: middle;
  }

  .has-alert header {
    margin-top: 40px;
  }

  .has-alert .vs-sidebar {
    top: 40px !important;
  }

  body.has-alert {
    margin-top: 0px !important;
  }

  .global-component .vs-alert--close {
    background: rgba(var(--vs-danger),1) !important;
    border: 1px solid white;
  }

  .has-alert .page-container {
    margin-top: 40px;
  }

  .has-alert .side-kick {
    top: 130px !important;
  }
</style>
